import { AGENCY_ROLES, AGENCY_ROLE_LABELS, ROLE_RANK, type AgencyRole } from '@aimmo/contracts';
import { Badge, Checkbox } from '@aimmo/ui';

/**
 * Roles most privileged first. They are stored in the order they were ticked,
 * and two colleagues with the same roles should not read differently because
 * somebody clicked the boxes the other way round.
 */
export function byRank(roles: readonly AgencyRole[]): AgencyRole[] {
  return [...roles].sort((a, b) => ROLE_RANK[b] - ROLE_RANK[a]);
}

/** A user's roles as chips. Admin stands out: it is the one that can change the others. */
export function RoleChips({ roles }: { roles: readonly AgencyRole[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {byRank(roles).map((role) => (
        <Badge key={role} tone={role === 'admin' ? 'accent' : 'outline'}>
          {AGENCY_ROLE_LABELS[role]}
        </Badge>
      ))}
    </div>
  );
}

/** The roles as a set of checkboxes, for inviting a colleague and for editing one. */
export function RoleCheckboxes({
  value,
  onChange,
}: {
  value: AgencyRole[];
  onChange: (roles: AgencyRole[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {AGENCY_ROLES.map((role) => (
        <label key={role} className="flex items-center gap-1.5 text-body text-ink">
          <Checkbox
            checked={value.includes(role)}
            onCheckedChange={(checked) =>
              onChange(
                checked === true ? [...value, role] : value.filter((current) => current !== role),
              )
            }
          />
          {AGENCY_ROLE_LABELS[role]}
        </label>
      ))}
    </div>
  );
}
